
import {Component, DoCheck, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { RegistrationService } from '@app/features/registration/services/registration.service';
import { Subject, takeUntil } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { Provider } from '@app/shared/interfaces/provider.interface';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';

@Component({
  selector: 'app-provider-multiselect',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatListModule,
    MatIconModule,
    MatButton,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatAccordion,
],
  templateUrl: './provider-multiselect.component.html',
  styleUrl: './provider-multiselect.component.scss',
})
export class ProviderMultiselectComponent implements OnDestroy, OnChanges, OnInit, DoCheck {
  @Input() required = false;
  @Input() label!: string;
  @Input() selectionFormControl!: FormControl;
  @Input() otherProviderNameFormControl!: FormControl;
  @Input() otherProviderNPIFormControl!: FormControl;
  @Input() removeProviderFormControl!: FormControl<any> | undefined;
  @Input() showError = true;
  @Input() clinicId!: string | null;
  @Input() selectedOptions!: string[] | undefined;
  @Input() hasOtherProvider: boolean = false;
  @Input() pendingRemovalProviderIds: string[] | undefined;
  @Input() showRequestRemovalClinicOption = true;
  @Input() originalClinicId: string | undefined;
  @Input() readOnly = false;

  onDestroy = new Subject<void>();
  listOptions: Provider[] = [];
  isOtherProviderSelected = false;
  isSelectAllProvidersChecked = false;
  hasAppliedSelectedOptions = false;
  readonly SELECT_ALL = '__SELECT_ALL__';

  clinicIsGroup = false;

  removalRequestedIds: Set<string> = new Set<string>();
  removalWasSelectedIds: Set<string> = new Set<string>();
  private removalProviderIdsSignature = '';

  constructor(
    private registrationService: RegistrationService
  ) {
  }

  ngOnInit(): void {
    this.syncOtherProviderSelectionFromPristineControls();
    this.syncRequiredValidator();
    this.selectionFormControl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.syncRemovalRequestedState();
        this.updateSelectAllChecked();
        this.syncOtherProviderSelectionFromPristineControls();
      });

    this.otherProviderNameFormControl?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.syncOtherProviderSelectionFromPristineControls());

    this.otherProviderNPIFormControl?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.syncOtherProviderSelectionFromPristineControls());

    this.removeProviderFormControl?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.syncRemovalRequestedState();
        this.updateSelectAllChecked();
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required'] && this.selectionFormControl) {
      this.syncRequiredValidator();
    }

    if (changes['clinicId']) {
      const previous = changes['clinicId'].previousValue;
      const current = changes['clinicId'].currentValue;
      if (previous !== current) {
        this.resetSelectionForClinicChange(!changes['clinicId'].firstChange);
        this.fetchProviderList();
      }
      if(previous === null && current !== null){
        this.selectionFormControl.setValidators([Validators.required]);
      }
    }

    if (changes['pendingRemovalProviderIds'] && !this.removeProviderFormControl) {
      this.syncRemovalRequestedState();
      this.updateSelectAllChecked();
    }

    if (changes['hasOtherProvider']) {
      this.syncOtherProviderSelectionFromPristineControls();
    }
  }

  private syncRequiredValidator(): void {
    if (this.required) {
      this.selectionFormControl.addValidators(Validators.required);
    } else {
      this.selectionFormControl.removeValidators(Validators.required);
    }
    this.selectionFormControl.updateValueAndValidity({ emitEvent: false });
  }

  ngDoCheck(): void {
    this.syncRemovalRequestedState();
  }

  fetchProviderList() {
    console.log('Fetching provider list for clinicId:', this.clinicId);
    if (!this.clinicId) {
      this.listOptions = [];
      return;
    }

    const clinicId = this.clinicId;

    this.syncRemovalRequestedState();

    this.registrationService.getProviderList()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((list: Provider[]) => {
        if (this.clinicId !== clinicId) {
          return;
        }

        this.listOptions = list.filter(provider => (provider.groupName) === clinicId || provider.providerAddressLine1 === clinicId);

        this.clinicIsGroup = this.listOptions.every(val => val.groupName === clinicId);

        this.syncOtherProviderSelectionFromPristineControls();
        // After fetching providers, apply any incoming selected options ONLY if the originalClinicId matches
        if (this.shouldApplySelectedOptions()) {
          this.applySelectedOptionsToSelection();
          this.sortListOptionsWithSelectedOnTop();
          this.hasAppliedSelectedOptions = true;
        } else {
          this.selectionFormControl.setValue(null, {emitEvent: false});
          this.isSelectAllProvidersChecked = false;
        }
      });
  }


  setOtherProviderInputValues(name: string|null, npi: string|null) {
    this.otherProviderNameFormControl.setValue(name, {emitEvent: false});
    this.otherProviderNPIFormControl.setValue(npi, {emitEvent: false});
  }

  setOtherProviderInputValidators() {
    if (!this.hasOtherProviderControls()) {
      return;
    }

    this.otherProviderNameFormControl.setValidators([Validators.required]);
    this.otherProviderNPIFormControl.setValidators([Validators.required, Validators.pattern(/^[0-9]{10}$/)]);
    this.otherProviderNameFormControl.updateValueAndValidity({ emitEvent: false });
    this.otherProviderNPIFormControl.updateValueAndValidity({ emitEvent: false });
  }

  clearOtherProviderInputValidators() {
    if (!this.hasOtherProviderControls()) {
      return;
    }

    this.otherProviderNameFormControl.clearValidators();
    this.otherProviderNameFormControl.markAsUntouched();
    this.otherProviderNameFormControl.updateValueAndValidity({ emitEvent: false });

    this.otherProviderNPIFormControl.clearValidators();
    this.otherProviderNPIFormControl.markAsUntouched();
    this.otherProviderNPIFormControl.updateValueAndValidity({ emitEvent: false });
  }

  allowOnlyDigits(event: KeyboardEvent) {
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  getProviderIds(): string[] {
    return this.listOptions.map(option => option.id);
  }

  isProviderSelected(id: string): boolean {
    return (this.selectionFormControl.value || []).includes(id);
  }

  onProvidersSelectionChange(event: MatSelectionListChange) {
    if (this.readOnly || this.selectionFormControl.disabled) {
      return;
    }
    const selected = event.source.selectedOptions.selected
      .map(opt => opt.value)
      .filter((value): value is string => typeof value === 'string' && value !== this.SELECT_ALL);
    this.isSelectAllProvidersChecked = this.getProviderIds().every(id => selected.includes(id));
    this.selectionFormControl.setValue(selected, {emitEvent: false});
    this.selectionFormControl.markAsDirty();
    this.selectionFormControl.markAsTouched();
    this.updateSelectAllChecked();
  }

  onSelectAllChange(event: MatSelectionListChange) {
    if (this.readOnly || this.selectionFormControl.disabled) {
      return;
    }
    const selected = event.source.selectedOptions.selected
      .map(opt => opt.value)
      .filter((value): value is string => value === this.SELECT_ALL);
    if (selected.length > 0) {
      const selectable = this.getProviderIds();
      this.isSelectAllProvidersChecked = true;
      this.selectionFormControl.setValue(
        [...selectable], {emitEvent: false}
      );
    } else {
      this.isSelectAllProvidersChecked = false;
      this.selectionFormControl.setValue( null, {emitEvent: false});
    }
    this.selectionFormControl.markAsDirty();
    this.selectionFormControl.markAsTouched();
  }

  onOtherProviderChange(event: MatSelectionListChange) {
    this.setOtherProviderSelected(event.source.selectedOptions.selected.length > 0);
  }

  onOtherProviderExpandedChange(isExpanded: boolean) {
    this.setOtherProviderSelected(isExpanded);
  }

  private setOtherProviderSelected(isSelected: boolean) {
    if (this.isOtherProviderSelected === isSelected) {
      return;
    }

    if (this.readOnly || this.selectionFormControl.disabled) {
      return;
    }

    this.isOtherProviderSelected = isSelected;
    if (this.isOtherProviderSelected) {
      this.setOtherProviderInputValidators();
      const values: string[] = (this.selectionFormControl.value || []);
      this.selectionFormControl.setValue([...values], {emitEvent: false});
    } else {
      this.clearOtherProviderInputValidators();
      this.setOtherProviderInputValues(null, null);
      this.selectionFormControl.setValue(
        (this.selectionFormControl.value || []), {emitEvent: false}
      );
    }
    this.selectionFormControl.markAsDirty();
    this.selectionFormControl.markAsTouched();
  }

  syncStateFromFormControls(): void {
    this.syncRemovalRequestedState();
    this.syncOtherProviderStateFromControls();
    this.updateSelectAllChecked();
  }

  private sortListOptionsWithSelectedOnTop(): void {
    if (!this.listOptions || this.listOptions.length === 0) { return; }

    const selectedIds = new Set<string>(
      Array.isArray(this.selectionFormControl?.value)
        ? (this.selectionFormControl.value as string[])
        : []
    );

    const selected: Provider[] = [];
    const unselected: Provider[] = [];
    for (const listOption of this.listOptions) {
      if (selectedIds.has(listOption.id)) {
        selected.push(listOption);
      } else {
        unselected.push(listOption);
      }
    }

    selected.sort((a, b) => {
      return a.lastName.localeCompare(b.lastName, undefined, { numeric: true, sensitivity: 'base' });
    });

    this.listOptions = [...selected, ...unselected];
  }

  private applySelectedOptionsToSelection() {
    const incomingIds = this.getSelectedIdsFromInput();
    if (!incomingIds.length) { return; }

    const includeOther = this.isOtherProviderSelected;

    this.selectionFormControl.setValue(
      includeOther ? [...incomingIds] : incomingIds,
      { emitEvent: false }
    );

    this.updateSelectAllChecked();
  }

  private getSelectedIdsFromInput(): string[] {
    if (!this.selectedOptions || !Array.isArray(this.selectedOptions)) { return []; }
    const validIds = new Set(this.getProviderIds());
    return this.selectedOptions.filter(id => validIds.has(id));
  }

  isRemovalRequested(id: string): boolean {
    return this.removalRequestedIds.has(id);
  }

  requestClinicRemoval(id: string): void {
    if (this.readOnly || this.selectionFormControl.disabled || this.removalRequestedIds.has(id)) { return; }
    const current: string[] = (this.selectionFormControl.value || []);
    if (current.includes(id)) {
      this.removalWasSelectedIds.add(id);
    }
    const next = current.filter(v => v !== id);
    this.selectionFormControl.setValue(next, { emitEvent: false });
    this.selectionFormControl.markAsDirty();
    this.selectionFormControl.markAsTouched();
    this.removalRequestedIds.add(id);
    if(this.removeProviderFormControl != null) {
      this.removeProviderFormControl.setValue([...this.removalRequestedIds], {emitEvent: false});
      this.removeProviderFormControl.markAsDirty();
    }
    this.updateSelectAllChecked();
  }

  undoClinicRemoval(id: string): void {
    if (this.readOnly || this.selectionFormControl.disabled || !this.removalRequestedIds.has(id)) { return; }
    this.removalRequestedIds.delete(id);

    const current: string[] = (this.selectionFormControl.value || []);
    if (this.removalWasSelectedIds.has(id)) {
      if (!current.includes(id)) { current.push(id); }
      this.removalWasSelectedIds.delete(id);
    }
    this.selectionFormControl.setValue(current, { emitEvent: false });
    this.selectionFormControl.markAsDirty();
    this.selectionFormControl.markAsTouched();
    if(this.removeProviderFormControl != null) {
      this.removeProviderFormControl.setValue([...this.removalRequestedIds], {emitEvent: false});
    }
    this.updateSelectAllChecked();
  }

  private updateSelectAllChecked(): void {
    const selected: string[] = (this.selectionFormControl.value || []);
    const availableIds = this.getProviderIds().filter(id => !this.removalRequestedIds.has(id));
    this.isSelectAllProvidersChecked = availableIds.length > 0 && availableIds.every(id => selected.includes(id));
  }

  private syncOtherProviderSelectionFromPristineControls(): void {
    if (!this.hasOtherProviderControls()) {
      this.isOtherProviderSelected = this.hasOtherProvider;
      return;
    }

    if (
      !this.selectionFormControl.pristine ||
      !this.otherProviderNameFormControl.pristine ||
      !this.otherProviderNPIFormControl.pristine
    ) {
      return;
    }

    this.syncOtherProviderStateFromControls();
  }

  private syncOtherProviderStateFromControls(): void {
    if (!this.hasOtherProviderControls()) {
      this.isOtherProviderSelected = this.hasOtherProvider;
      return;
    }

    this.isOtherProviderSelected = this.hasOtherProvider ||
      this.hasOtherProviderFormValues();

    if (this.isOtherProviderSelected) {
      this.setOtherProviderInputValidators();
    } else {
      this.clearOtherProviderInputValidators();
    }
  }

  private hasOtherProviderControls(): boolean {
    return !!this.otherProviderNameFormControl && !!this.otherProviderNPIFormControl;
  }

  private hasOtherProviderFormValues(): boolean {
    return this.hasControlValue(this.otherProviderNameFormControl.value)
      || this.hasControlValue(this.otherProviderNPIFormControl.value);
  }

  private hasControlValue(value: unknown): boolean {
    return value !== null && value !== undefined && String(value).trim().length > 0;
  }

  private syncRemovalRequestedState(): void {
    const providerIds = this.removeProviderFormControl != null
      ? this.removeProviderFormControl.value
      : this.pendingRemovalProviderIds;

    const validProviderIds = (Array.isArray(providerIds) ? providerIds : [])
      .filter((providerId): providerId is string => typeof providerId === 'string' && providerId.length > 0);
    const nextSignature = validProviderIds.join('|');

    if (nextSignature === this.removalProviderIdsSignature) {
      return;
    }

    this.removalProviderIdsSignature = nextSignature;
    this.removalRequestedIds = new Set<string>(
      validProviderIds
    );
  }

  private resetSelectionForClinicChange(clearFormControls: boolean): void {
    this.listOptions = [];
    this.isSelectAllProvidersChecked = false;
    this.hasAppliedSelectedOptions = false;
    this.removalRequestedIds.clear();
    this.removalWasSelectedIds.clear();
    this.removalProviderIdsSignature = '';

    if (!clearFormControls) {
      return;
    }

    this.selectionFormControl.setValue(null, { emitEvent: false });

    if (this.removeProviderFormControl != null && this.removeProviderFormControl.dirty) {
      this.removeProviderFormControl.setValue([], { emitEvent: false });
    }
  }

  private shouldApplySelectedOptions(): boolean {
    return !!this.originalClinicId &&
      this.clinicId === this.originalClinicId &&
      !this.hasAppliedSelectedOptions;
  }
}
